// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type BaseHolding } from '../commons';

export interface CitecApi {
  getUniverse: (params: UniverseId) => Promise<Universe>;

  postUniverse: (payload: Universe) => Promise<Universe>;

  universeAll: () => Promise<UniverseAllResp[]>;
}

export interface UniverseId {
  universe_id: number;
}

export interface PortfolioId {
  portfolio_id: number;
}

export interface UniverseAllResp extends UniverseId {
  description: string;
  creation_date: string;
}

export interface Universe extends UniverseAllResp {
  universe_base: UniverseBase;
  factors_filter: FactorsFilter;
  sectors_filter: string[];
  esg_exclusions: string[];
  esg_scoring: EsgScoring;
  esg_not_informed: boolean;
  to_exclude: string[];
  to_include: string[];
}

export interface UserEmail {
  email: string;
}

export interface UserBase {
  /** Advisor's first or given name. */
  first_name: string | null;
  /** Advisor's second or middle name. */
  second_name: string | null;
  /** Advisor's first (paternal) surname. */
  first_surname: string | null;
  /** Advisor's second (maternal) surname. */
  second_surname: string | null;
  phone_number: string | null;
  user_id?: number | null;
}

export interface Organization {
  flag_admin: boolean;
  organization_id: number;
  organization: string;
}

export interface LoginType {
  is_organization: boolean;
  organization_name: string;
  organization_id: string;
}

export enum AdvisorRole {
  SilverAlpha = 'silver_alpha',
  OperationsCitec = 'operations_citec',
  AdvisorNotAgent = 'advisor_not_agent',
  Agent = 'agent',
}

export interface CitecUser extends UserEmail, UserBase {
  name?: string | null;
  user_id: number;
  organizations: Organization[];
  login_type: LoginType;
  role?: AdvisorRole | null;
}

interface Boundaries {
  lower: number[];
  upper: number[];
}

interface FactorsFilter extends Boundaries {
  factor: string[];
}

interface EsgScoring extends Boundaries {
  filter: string[];
}

interface UniverseBase {
  name: string;
  description: string;
  ticker: string | null;
}

export enum TickerTagFeature {
  PortfolioPositions = 'portfolio-positions',
  SelectedAssets = 'watchlist',
  AggregatorPositions = 'aggregator-positions',
}

export interface TickerTag {
  tickers: string[];
  tag: string;
  feature: TickerTagFeature;
  description?: string | null;
}

export interface SingleTickerTag {
  tag: string;
  ticker: string;
}

export interface SearchTickerTag {
  ticker?: string;
  tag?: string;
  feature: TickerTagFeature;
}

export interface TickersTags {
  ticker: string;
  name: string;
  assetClass?: string;
  isin?: string | null;
  tags: string[];
  url_logo?: string | null;
}

export interface WatchlistResp {
  ticker: string;
  name: string;
  isin: string;
  asset_type: string;
  saved_at?: string;
}

export interface RemoveWatchlistItem {
  ticker: string;
}

export interface SavingHolding extends BaseHolding {
  weight: number;
  outstanding_balance?: number;
}

export interface SearchMarketplaceFilters {
  client_residency?: string;
  esg?: string;
  asset_manager?: string;
  risk_level?: string;
}

export interface PortfolioDetails {
  portfolio_id: number;
  portfolio: string;
  benchmark: string;
  creation_date: string;
  optimization_status: string;
  client_residency: string;
  esg: string;
  asset_manager: string;
  manager: string;
  risk_level: string;
  description: string;
  holdings: PortfolioHolding[];
}

export interface PortfolioHolding {
  ticker: string;
  outstanding_balance: number;
  unrealized_capital_gains: number;
}

export type Marketplace = PortfolioDetails[];
